import { ClickAwayListener, createStyles, makeStyles, Typography } from '@material-ui/core';
import { Input } from 'components/UI/V2/Forms/Input/Input';
import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { AddressSuggestion, SelectedPlace, usePlacesAutocomplete } from 'hooks/usePlacesAutocomplete';
import { SessionBusinessArea } from 'model/ServiceSession';
import { toRegionCode } from 'utils/regionCode';

export interface AddressAutocompleteProps {
    area: SessionBusinessArea;
    value: string;
    onChange: (value: string) => void;
    onSelect: (place: SelectedPlace) => void;
}

const useStyles = makeStyles(() =>
    createStyles({
        root: {
            position: 'relative',
            flexShrink: 0
        },
        menu: {
            top: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            zIndex: 2,
            position: 'absolute',
            overflowY: 'auto',
            maxHeight: 240,
            borderRadius: 12,
            backgroundColor: '#fff',
            border: '1px solid #D4D4D4',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)'
        },
        option: {
            'display': 'block',
            'width': '100%',
            'border': 0,
            'padding': '10px 16px',
            'textAlign': 'left',
            'fontSize': 14,
            'lineHeight': 1.4,
            'cursor': 'pointer',
            'background': 'none',
            'fontFamily': 'Poppins',

            '&:hover': {
                backgroundColor: '#F8F8F8'
            }
        },
        empty: {
            padding: '10px 16px',
            fontSize: 14,
            color: '#6B6B6B'
        }
    })
);

export const AddressAutocomplete: React.FC<AddressAutocompleteProps> = props => {
    const classes = useStyles();
    const countryCode = useSelector(
        (state: RootState) =>
            state.marketplace.marketplace?.basics?.address?.country?.code ||
            state.marketplace.marketplace?.basics?.address?.country?.id
    );
    const { suggestions, ready, selectSuggestion, clearSkip } = usePlacesAutocomplete(
        props.value,
        toRegionCode(countryCode)
    );
    const [open, setOpen] = React.useState(false);

    const changeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        clearSkip();
        props.onChange(event.target.value);
        setOpen(true);
    };

    const selectHandler = async (suggestion: AddressSuggestion) => {
        const place = await selectSuggestion(suggestion);
        if (!place) return;

        props.onSelect(place);
        setOpen(false);
    };

    const showMenu = open && props.value.trim().length > 0 && (ready || suggestions.length > 0);

    return (
        <ClickAwayListener onClickAway={() => setOpen(false)}>
            <div className={classes.root}>
                <Input
                    value={props.value}
                    label="Address"
                    placeholder="Search address"
                    autoComplete="off"
                    id={`${props.area}-zone-address-input`}
                    onChange={changeHandler}
                    onFocus={() => setOpen(true)}
                />

                {showMenu && (
                    <div
                        className={classes.menu}
                        id={`${props.area}-zone-address-suggestions`}
                    >
                        {suggestions.map(suggestion => (
                            <button
                                key={suggestion.id}
                                type="button"
                                className={classes.option}
                                onMouseDown={event => event.preventDefault()}
                                onClick={() => selectHandler(suggestion)}
                            >
                                {suggestion.label}
                            </button>
                        ))}

                        {suggestions.length === 0 && (
                            <Typography className={classes.empty}>No matching addresses</Typography>
                        )}
                    </div>
                )}
            </div>
        </ClickAwayListener>
    );
};
